import React from 'react';
import { Box, IconButton, List, ListItem, styled } from '@mui/material';
import { KeyboardArrowLeftRounded, KeyboardArrowRightRounded } from '@mui/icons-material';
import { AssessmentsPageIndicator } from 'components/AssessmentsPageIndicator/AssessmentsPageIndicator';
import { ASSESSMENT_PAGE_INDICATOR_SIZE } from 'features/assessments/constants';
import { AssessmentsPage } from 'features/assessments/types';
import { useAssessmentsDesktopPageNavigation } from 'features/assessments/hooks/useAssessmentsDesktopPageNavigation';

interface DesktopAssessmentsPageNavigationProps {
  /**
   * Current page number
   */
  currentPage: number;
  /**
   * Array of page data with question counts
   */
  pages: Array<AssessmentsPage>;
  /**
   * Callback when a page indicator is clicked
   */
  onPageClick: (page: number) => void;
}

const Container = styled(Box)({
  flex: 1,
  display: 'flex',
  overflow: 'hidden',
  position: 'relative',
  // Keep room for at least one page indicator plus both scroll chevrons so the strip
  // never collapses to a dead chevron with no page numbers behind it.
  minWidth: ASSESSMENT_PAGE_INDICATOR_SIZE * 2,
});

const ListContainer = styled(List)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  overflowX: 'auto',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  padding: 0,
}));

const PageListItem = styled(ListItem)(() => ({
  padding: 0,
}));

const ScrollButton = styled(IconButton)(() => ({
  position: 'absolute',
  zIndex: 1,
  height: ASSESSMENT_PAGE_INDICATOR_SIZE,
  borderRadius: 0,
  padding: 0,
}));

const LeftScrollButton = styled(ScrollButton)(({ theme }) => ({
  left: 0,
  background: `linear-gradient(to right, ${theme.palette.common.white} 60%, transparent)`,
}));

const RightScrollButton = styled(ScrollButton)(({ theme }) => ({
  right: 0,
  background: `linear-gradient(to right, transparent, ${theme.palette.common.white} 40%)`,
}));

export const DesktopAssessmentsPageNavigation = ({
  currentPage,
  pages,
  onPageClick,
}: DesktopAssessmentsPageNavigationProps) => {
  const { scrollRef, showLeftArrow, showRightArrow, scrollLeft, scrollRight } =
    useAssessmentsDesktopPageNavigation(pages);

  return (
    <Container>
      {showLeftArrow && (
        <LeftScrollButton onClick={scrollLeft} aria-label="Scroll to previous pages" size="large">
          <KeyboardArrowLeftRounded fontSize="large" aria-hidden="true" />
        </LeftScrollButton>
      )}
      <ListContainer ref={scrollRef}>
        {pages.map((page) => (
          <PageListItem key={page.pageNumber}>
            <AssessmentsPageIndicator
              pageNumber={page.pageNumber}
              questionCount={page.questionCount}
              questionsCompleted={page.questionsCompleted}
              isActive={page.pageNumber === currentPage}
              onClick={onPageClick}
            />
          </PageListItem>
        ))}
      </ListContainer>
      {showRightArrow && (
        <RightScrollButton onClick={scrollRight} aria-label="Scroll to next pages" size="large">
          <KeyboardArrowRightRounded fontSize="large" aria-hidden="true" />
        </RightScrollButton>
      )}
    </Container>
  );
};
